import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { TablesInsert } from "@/integrations/supabase/types";

export default function CompanyUsersPage() {
  const { profile, user } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePassword, setInvitePassword] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);

  // Buscar usuários da empresa
  useEffect(() => {
    async function fetchUsers() {
      if (!profile?.company_id) return;
      setLoading(true);
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, company_id, created_at")
        .eq("company_id", profile.company_id);
      if (data) setUsers(data);
      setLoading(false);
    }
    fetchUsers();
  }, [profile, dialogOpen]);

  // Adicionar novo usuário (convite)
  async function handleInvite() {
    setInviteLoading(true);
    try {
      // 1. Cria usuário no Auth
      const { data: newUser, error: createError } = await supabase.auth.admin.createUser({
        email: inviteEmail,
        password: invitePassword,
        user_metadata: { full_name: inviteName }
      });
      if (createError) throw createError;
      const userId = newUser.user?.id;
      if (!userId) throw new Error("Não foi possível obter o ID do usuário");

      // 2. Cria perfil associado à empresa (ou atualiza se já existir)
      const profileInsert: TablesInsert<'profiles'> = {
        id: userId,
        full_name: inviteName,
        company_id: profile.company_id
      };
      await supabase.from("profiles").upsert(profileInsert);

      toast.success("Usuário adicionado à empresa!");
      setDialogOpen(false);
      setInviteName("");
      setInviteEmail("");
      setInvitePassword("");
    } catch (error: any) {
      toast.error(error.message || "Erro ao adicionar usuário");
    } finally {
      setInviteLoading(false);
    }
  }

  // Remover usuário da empresa
  async function handleRemove(userId: string) {
    if (!window.confirm("Tem certeza que deseja remover este usuário da empresa?")) return;
    await supabase.from("profiles").update({ company_id: null }).eq("id", userId);
    setUsers(users.filter(u => u.id !== userId));
    toast.success("Usuário removido da empresa.");
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Usuários da Empresa</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex justify-end">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>Adicionar Usuário</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Adicionar Usuário à Empresa</DialogTitle>
              </DialogHeader>
              <Input
                type="text"
                placeholder="Nome do usuário"
                value={inviteName}
                onChange={e => setInviteName(e.target.value)}
                className="mb-2"
              />
              <Input
                type="email"
                placeholder="E-mail do usuário"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                className="mb-2"
              />
              <Input
                type="password"
                placeholder="Senha inicial"
                value={invitePassword}
                onChange={e => setInvitePassword(e.target.value)}
                className="mb-2"
              />
              <Button onClick={handleInvite} disabled={inviteLoading || !inviteEmail || !inviteName || !invitePassword}>
                {inviteLoading ? "Adicionando..." : "Adicionar"}
              </Button>
            </DialogContent>
          </Dialog>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full border text-sm">
            <thead>
              <tr className="bg-muted">
                <th className="p-2 text-left">Nome</th>
                <th className="p-2 text-left">ID</th>
                <th className="p-2 text-left">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3}>Carregando...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={3}>Nenhum usuário encontrado.</td></tr>
              ) : users.map(u => (
                <tr key={u.id} className="border-b">
                  <td className="p-2">{u.full_name || u.id}</td>
                  <td className="p-2">{u.id}</td>
                  <td className="p-2">
                    <Button size="sm" variant="outline" onClick={() => handleRemove(u.id)} disabled={u.id === user?.id}>
                      Remover
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
